import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Rnd } from 'react-rnd';
import { useQuery, useMutation } from '@apollo/client';
import { eq, find, get } from 'lodash';
import PropTypes from 'prop-types';
import { CLIENT_STATE, GENERAL_SETTINGS, PAGE_DATA, UPDATE_PAGE_DATA, UPDATE_GENERAL_SETTINGS, SET_CLIENT_STATE } from '../../../graphqls';

export default function ImageEditor({ width: containerWidth, height: containerHeight }) {
  const dotImage = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
  const { pathname } = useLocation();
  const paths = pathname.split('/');
  const img = new Image();
  const { loading: loadingClientState, data: { modalTypeShown, selectedPlanNodeId } = {} } = useQuery(CLIENT_STATE);
  const {
    loading: loadingGeneralSettings,
    data: { generalSettings: { logo: { image: logo } = {}, banner: { image: commonBanner, position: headerPosition } = {} } = {} } = {},
  } = useQuery(GENERAL_SETTINGS);
  const {
    loading: loadingPageData,
    data: {
      pageData: { cache: { banner: { image: bannerImage, position: bannerPosition } = {}, children, parentPlanNodeId } = {} } = {},
    } = {},
  } = useQuery(PAGE_DATA, { variables: { name: eq(paths[1], 'home') ? 'home' : paths[2] } });
  const [setState] = useMutation(SET_CLIENT_STATE);
  const [updatePageData] = useMutation(UPDATE_PAGE_DATA, {
    refetchQueries: [{ query: PAGE_DATA, variables: { name: parentPlanNodeId || 'home' } }],
  });
  const [updateGeneralSettings] = useMutation(UPDATE_GENERAL_SETTINGS, { refetchQueries: [{ query: GENERAL_SETTINGS }] });
  const [currentImage, setCurrentImage] = useState('');
  const [scaledSize, setScaledSize] = useState({ width: 1, height: 1 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragAxis, setDragAxis] = useState('y');
  const [backgroundPosition, setBackgroundPosition] = useState('center');
  const [rejectMessage, setRejectMessage] = useState('');
  const onDrop = (files) => {
    const reader = new FileReader();

    try {
      reader.onloadend = ({ target: { result } }) => {
        img.src = result;
        setCurrentImage(result);
      };

      reader.readAsDataURL(files[0]);
      setRejectMessage('');
    } catch (e) {
      console.warn(e);
      setRejectMessage(' Failed to upload. Max file size exceeded (10MB).');
    }
  };
  const onDropRejected = () => setRejectMessage(' Failed to upload. Wrong file type (.jpeg or .png) or max file size exceeded (10MB).');
  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    onDropRejected,
    // maxSize: 10485760,
    accept: 'image/jpeg, image/png',
    noClick: !!currentImage,
  });

  if (loadingClientState || loadingGeneralSettings || loadingPageData) {
    return '';
  }

  const disableDragging = eq(modalTypeShown, 'logo');
  img.onload = () => {
    const { width: imgWidth, height: imgHeight } = img;

    switch (modalTypeShown) {
      case 'logo':
        setDragAxis('none');
        setScaledSize({ height: imgHeight, width: imgWidth });
        setPosition({ x: (containerWidth - imgWidth) / 2, y: (containerHeight - imgHeight) / 2 });
        break;
      default:
        let x = 0;
        let y = 0;
        let currentPosition = 'center';

        switch (modalTypeShown) {
          case 'bannerImage':
            currentPosition = bannerPosition;
            break;
          case 'featureImage':
            currentPosition = get(find(children, ['planNodeId', selectedPlanNodeId]), ['thumbnail', 'position']);
            break;
          case 'commonBanner':
            currentPosition = headerPosition;
            break;
          default:
            break;
        }

        switch (imgWidth / imgHeight > containerWidth / containerHeight) {
          case true:
            const scaledWidth = (imgWidth * containerHeight) / imgHeight;
            setScaledSize({ height: containerHeight, width: scaledWidth });
            setDragAxis('x');

            if (currentPosition === 'left') {
              x = 0;
            } else if (currentPosition === 'right') {
              x = containerWidth - scaledWidth;
            } else if (currentPosition.indexOf(' ') > 0) {
              const [, currentPercent] = currentPosition.split(' ');
              y = (containerWidth - scaledWidth) * (1 + Number(currentPercent.replace('%', '')));
            } else {
              x = (containerWidth - scaledWidth) / 2;
            }

            setPosition({ x, y: 0 });
            break;
          case false:
            const scaledHeight = (imgHeight * containerWidth) / imgWidth;
            setScaledSize({ height: scaledHeight, width: containerWidth });
            setDragAxis('y');

            if (currentPosition === 'top') {
              y = 0;
            } else if (currentPosition === 'bottom') {
              y = containerHeight - scaledHeight;
            } else if (currentPosition.indexOf(' ') > 0) {
              const [, currentPercent] = currentPosition.split(' ');
              y = (containerHeight - scaledHeight) * (1 + Number(currentPercent.replace('%', '')));
            } else {
              y = (containerHeight - scaledHeight) / 2;
            }

            setPosition({ x: 0, y });
            break;
          default:
            break;
        }
        break;
    }

    setBackgroundPosition('center');
  };

  if (!currentImage) {
    let savedImage = '';

    switch (modalTypeShown) {
      case 'logo':
        savedImage = logo;
        break;
      case 'commonBanner':
        savedImage = commonBanner;
        break;
      case 'bannerImage':
        savedImage = bannerImage;
        break;
      case 'featureImage':
        savedImage = get(find(children, ['planNodeId', selectedPlanNodeId]), ['thumbnail', 'image']);
        break;
      default:
        break;
    }

    if (savedImage) {
      img.src = savedImage;
      setCurrentImage(savedImage);
    }
  }

  const onSave = () => {
    switch (modalTypeShown) {
      case 'logo':
        updateGeneralSettings({ variables: { logo: { image: currentImage || '' } } });
        break;
      case 'commonBanner':
        updateGeneralSettings({ variables: { banner: { image: currentImage, position: backgroundPosition } } });
        break;
      case 'featureImage':
        updatePageData({
          variables: { name: selectedPlanNodeId, cache: { thumbnail: { image: currentImage, position: backgroundPosition } } },
        });
        break;
      case 'bannerImage':
        updatePageData({
          variables: { cache: { banner: { image: currentImage === dotImage ? '' : currentImage, position: backgroundPosition } } },
        });
        break;
      default:
        break;
    }

    setState({ variables: { modalTypeShown: '' } });
  };
  const onDragStop = (e, { x, y }) => {
    const additionalWidth = containerWidth - scaledSize.width;
    const additionalHeight = containerHeight - scaledSize.height;

    switch (dragAxis) {
      case 'x':
        if (x > 0) {
          setPosition({ x: 0, y: 0 });
          setBackgroundPosition('left');
        } else if (x < additionalWidth) {
          setPosition({ x: additionalWidth, y: 0 });
          setBackgroundPosition('right');
        } else {
          setPosition({ x, y: 0 });

          if (x > additionalWidth / 2) {
            setBackgroundPosition(`left -${x / (additionalWidth / 2)}%`);
          } else if (x < additionalWidth / 2) {
            setBackgroundPosition(`right ${(x - additionalWidth) / (additionalWidth / 2)}%`);
          } else if (x === additionalWidth / 2) {
            setBackgroundPosition('center');
          }
        }
        break;
      case 'y':
        if (y > 0) {
          setPosition({ x: 0, y: 0 });
          setBackgroundPosition('top');
        } else if (y < containerHeight - scaledSize.height) {
          setPosition({ x: 0, y: containerHeight - scaledSize.height });
          setBackgroundPosition('bottom');
        } else {
          setPosition({ x: 0, y });

          if (y > additionalHeight / 2) {
            setBackgroundPosition(`top -${y / (additionalHeight / 2)}%`);
          } else if (y < additionalHeight / 2) {
            setBackgroundPosition(`bottom ${(y - additionalHeight) / (additionalHeight / 2)}%`);
          } else if (y === additionalHeight / 2) {
            setBackgroundPosition('center');
          }
        }
        break;
      default:
        break;
    }
  };
  const onRemove = () => {
    setCurrentImage(dotImage);
  };
  const onReplace = () => open();
  const onReset = () => {
    setBackgroundPosition('center');

    const { width: scaledWidth, height: scaledHeight } = scaledSize;

    switch (dragAxis) {
      case 'x':
        setPosition({ x: (containerWidth - scaledWidth) / 2, y: 0 });
        break;
      case 'y':
        setPosition({ x: 0, y: (containerHeight - scaledHeight) / 2 });
        break;
      default:
        break;
    }
  };

  return (
    <div className="process-image">
      <div className="image-container">
        {currentImage ? (
          <div
            style={{
              overflow: 'hidden',
              position: 'relative',
              height: `${containerHeight}px`,
              width: `${containerWidth}px`,
              margin: '0 auto',
              background: `
                repeating-linear-gradient(transparent, transparent 10px, rgba(0,0,0,0.05) 10px, rgba(0,0,0,0.05) 20px),
                repeating-linear-gradient(90deg, transparent, transparent 10px, rgba(0,0,0,0.05) 10px, rgba(0,0,0,0.05) 20px)
              `,
            }}
          >
            <Rnd
              position={position}
              size={scaledSize}
              onDragStop={onDragStop}
              style={{
                backgroundImage: `url(${currentImage})`,
                backgroundSize: modalTypeShown === 'logo' ? undefined : 'cover',
                backgroundRepeat: 'no-repeat',
                cursor: modalTypeShown === 'logo' ? undefined : 'move',
              }}
              disableDragging={disableDragging}
            />
          </div>
        ) : (
          <section className="dropzone">
            <div {...getRootProps({ className: 'dropzone-container' })}>
              <input {...getInputProps()} />
              <p>Drag your image here or upload a file.</p>
              <FontAwesomeIcon icon={['fal', 'upload']} />
            </div>
          </section>
        )}
      </div>
      <span className="error">{rejectMessage}</span>
      <div {...(currentImage ? getRootProps({ className: 'button-container' }) : { className: 'button-container' })}>
        {currentImage ? <input {...getInputProps()} /> : ''}
        <button type="button" className="btn mint-btn" onClick={onSave}>
          Save
        </button>
        {!!currentImage && (
          <button type="button" className="btn mint-btn btn-remove" onClick={onRemove}>
            Remove
          </button>
        )}
        <div className="side-button-container">
          {!!currentImage && (
            <>
              <button type="button" className="btn-replace" onClick={onReplace}>
                Replace
              </button>
              {!disableDragging && (
                <button type="button" className="btn-reset" onClick={onReset}>
                  Reset
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

ImageEditor.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
};
